import { firstValueFrom, timeout } from 'rxjs'
import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { LocalStorage } from 'ngx-store'

import { LoggerFactory } from 'common/log'
import { WindowLocationService } from 'common/util'
import { ModalService } from 'common/util.modals'

import { ElementsClientNotRunningModalComponent } from '../components/elementsClientNotRunningModal.component'

class ClientAuthRejected extends Error {
  constructor () {
    super('ELEMENTS Client rejected authorization')
  }
}

@Injectable({ providedIn: 'root' })
export class ElementsClientService {
  logger = inject(LoggerFactory).create('ElementsClientService')
  connected = false
  port: null | number
  private initialPort = 31335
  private maxPorts = 4

  @LocalStorage('clientAccessKey') storedClientAccessKey = ''

  constructor (
    private http: HttpClient,
    private modal: ModalService,
    private windowLocation: WindowLocationService,
  ) { }

  async detect (): Promise<number | null> {
    this.logger.debug('Detecting')
    for (let port = this.initialPort; port < this.initialPort + this.maxPorts; port++) {
      try {
        const response = await this.testConnection(port)
        this.logger.info(`Detected ELEMENTS Client ${response.version} on port ${port}`)
        this.connected = true
        this.port = port
        return port
      } catch {
        // Nothing.
      }
    }
    this.port = null
    this.logger.info('No ELEMENTS client found')
    return null
  }

  async testConnection (port: number | null): Promise<any> {
    const query$ = this.http.jsonp(`http://localhost:${port}/app/info`, 'callback')
    return firstValueFrom(query$.pipe(timeout(1000)))
  }

  async authorize (): Promise<void> {
    const response: any = await firstValueFrom(this.http
      .jsonp(
        `http://localhost:${this.port}/access/request?key=${this.storedClientAccessKey}&name=ELEMENTS`,
        'callback',
      ))
    if (response.state !== 'allowed') {
      throw new ClientAuthRejected()
    }
    this.storedClientAccessKey = response.key
  }

  async ensureConnected (): Promise<void> {
    while (true) {
      await this.detect()

      if (this.port) {
        break
      }

      if (!await this.modal.open(ElementsClientNotRunningModalComponent, null)) {
        break
      }
    }
  }

  async ensureAuthorized (): Promise<void> {
    while (true) {
      try {
        await this.authorize()
        break
      } catch (error) {
        if (error instanceof ClientAuthRejected) {
          continue
        }
        throw error
      }
    }
  }

  async refresh (): Promise<void> {
    if (this.connected) {
      try {
        await this.testConnection(this.port)
      } catch {
        this.connected = false
        await this.detect()
      }
    } else {
      await this.detect()
    }
  }

  async mount (workspaceId: number): Promise<void> {
    await this.ensureConnected()
    await this.ensureAuthorized()
    await firstValueFrom(this.http.jsonp(
      `http://localhost:${this.port}/app/mount/${workspaceId}?key=${this.storedClientAccessKey}`,
      'callback',
    ))
  }

  async isMounted (workspaceId: number): Promise<boolean> {
    await this.ensureConnected()
    await this.ensureAuthorized()
    const response = await firstValueFrom(this.http.jsonp<{ mounted: boolean }>(
      `http://localhost:${this.port}/app/is-mounted/${workspaceId}?key=${this.storedClientAccessKey}`,
      'callback',
    ))
    return response.mounted
  }

  async loadAAF (fileId: number): Promise<string> {
    await this.ensureConnected()
    await this.ensureAuthorized()
    const response = await firstValueFrom(this.http.jsonp<{ path: string }>(
      `http://localhost:${this.port}/app/load-aaf/${fileId}?key=${this.storedClientAccessKey}`,
      'callback',
    ))

    return response.path
  }

  activateURI (uri: string): void {
    this.logger.info('activating URI', uri)
    this.windowLocation.navigateToExternalURI(uri)
  }

  generateURI (command: string, params: Record<string, any>): string {
    const query = Object.keys(params).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k])).join('&')
    return `elements-client://${command}?${query}`
  }

  showFile (path: string, workspaceId: number | null = null): void {
    this.activateURI(this.generateURI('show', { path, workspace_id: workspaceId }))
  }

  browseToFile (path: string, workspaceId: number): void {
    this.activateURI(this.generateURI('browse', { path, workspace_id: workspaceId }))
  }

  tryStart (): void {
    this.activateURI(this.generateURI('start', {}))
  }
}
